/**
 * Settings → SSO → Strict Mode (ADR-025 Phase 4).
 *
 * Per-tenant strict-SSO toggle. When True, password sign-in is
 * refused for non-break-glass accounts (per ADR-025 D3+D4).
 *
 * Page surfaces:
 *   1. Current status + activation audit (when activated; by whom)
 *   2. 3-row pre-flight checklist (D2):
 *        - Active SSO config + plan-allowed
 *        - At least one break-glass admin (≥1 active worker with
 *          linked auth_user_id; Phase 4.B will tighten the local
 *          check to require a User with force_password_login=True)
 *        - Every active worker has signed in via SSO at least once
 *   3. Flip toggle (disabled until ready=True OR strict already on)
 *   4. Emergency-disable dialog (red CTA; required reason ≥10 chars)
 *
 * §13 chrome verbatim per svdy-ui.md: px=42 py=12, 1px border +
 * divider, no shadows, buttons borderRadius:1 textTransform:none
 * size:small.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  ShieldOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';

import { ApiError, systemApi } from '../../../api';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';
import { activeLocale } from '../../../utils/datetime';
import type {
  SsoStrictModePreflightCheck,
  SsoStrictModePreflightResponse,
  SsoStrictModeStatus,
} from '../../../api/system';


const CHECK_KEY: Record<SsoStrictModePreflightCheck['id'], string> = {
  active_sso_config: 'activeSsoConfig',
  break_glass_admin: 'breakGlassAdmin',
  all_workers_migrated: 'allWorkersMigrated',
};


const SSOStrictMode: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<SsoStrictModeStatus | null>(null);
  const [preFlight, setPreFlight] = useState<SsoStrictModePreflightResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [flipping, setFlipping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Emergency-disable dialog state
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [emergencyReason, setEmergencyReason] = useState('');
  const [emergencyDisabling, setEmergencyDisabling] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, pf] = await Promise.all([
        systemApi.systemSsoStrictModeApi.status(),
        systemApi.systemSsoStrictModeApi.preFlight(),
      ]);
      setStatus(s);
      setPreFlight(pf);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('system:ssoStrictMode.errors.loadState'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleFlip = async (enable: boolean) => {
    setFlipping(true);
    setError(null);
    try {
      const next = await systemApi.systemSsoStrictModeApi.flip(enable);
      setStatus(next);
      setToast(
        enable
          ? t('system:ssoStrictMode.toastActivated')
          : t('system:ssoStrictMode.toastDeactivated'),
      );
      // Re-fetch pre-flight to reflect any state changes.
      const pf = await systemApi.systemSsoStrictModeApi.preFlight();
      setPreFlight(pf);
    } catch (e) {
      // 422 from server when pre-flight checks fail at write time.
      if (e instanceof ApiError && e.status === 422) {
        setError(t('system:ssoStrictMode.errors.preflightFailed'));
        // Re-fetch pre-flight to show admin the latest state.
        try {
          const pf = await systemApi.systemSsoStrictModeApi.preFlight();
          setPreFlight(pf);
        } catch {
          // ignore secondary failure
        }
      } else {
        setError(
          e instanceof ApiError ? translateApiError(e, t) : t('system:ssoStrictMode.errors.flipFailed'),
        );
      }
    } finally {
      setFlipping(false);
    }
  };

  const handleEmergencyDisable = async () => {
    setEmergencyDisabling(true);
    setError(null);
    try {
      const next = await systemApi.systemSsoStrictModeApi.emergencyDisable(
        emergencyReason,
      );
      setStatus(next);
      setToast(t('system:ssoStrictMode.toastEmergencyDisabled'));
      setEmergencyOpen(false);
      setEmergencyReason('');
      const pf = await systemApi.systemSsoStrictModeApi.preFlight();
      setPreFlight(pf);
    } catch (e) {
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('system:ssoStrictMode.errors.emergencyFailed'),
      );
    } finally {
      setEmergencyDisabling(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 8,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  const ready = preFlight?.ready ?? false;
  const strictOn = status?.strict_mode ?? false;
  const reasonValid = emergencyReason.trim().length >= 10;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, fontSize: '1.125rem' }}
      >
        {t('system:ssoStrictMode.title')}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2 }}
      >
        {t('system:ssoStrictMode.subtitle')}
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {/* Status card */}
      <Box
        sx={{
          width: '100%',
          mb: 2,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ShieldOutlined
              sx={{
                color: strictOn ? 'success.main' : 'text.disabled',
                fontSize: 26,
              }}
            />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {strictOn ? t('system:ssoStrictMode.statusOn') : t('system:ssoStrictMode.statusOff')}
              </Typography>
              {strictOn && status?.activated_at ? (
                <Typography variant="caption" color="text.secondary">
                  {t('system:ssoStrictMode.activatedAtPrefix')}{' '}
                  {new Date(status.activated_at).toLocaleString(activeLocale())}
                  {status.activated_by_user_id
                    ? ` ${t('system:ssoStrictMode.activatedByPrefix')} ${status.activated_by_user_id}`
                    : null}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t('system:ssoStrictMode.passwordAllowed')}
                </Typography>
              )}
            </Box>
          </Stack>
          {strictOn ? (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleFlip(false)}
                disabled={flipping}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                }}
              >
                {t('system:ssoStrictMode.disableButton')}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => setEmergencyOpen(true)}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                }}
              >
                {t('system:ssoStrictMode.emergencyDisableButton')}
              </Button>
            </Stack>
          ) : (
            <Button
              size="small"
              variant="contained"
              onClick={() => handleFlip(true)}
              disabled={flipping || !ready}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
              }}
            >
              {t('system:ssoStrictMode.activateButton')}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Pre-flight checklist */}
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, mb: 1 }}
      >
        {t('system:ssoStrictMode.preflightTitle')}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1.5 }}
      >
        {t('system:ssoStrictMode.preflightCaption')}
      </Typography>

      <Stack
        direction="column"
        spacing={1}
        sx={{
          width: '100%',
          mb: 2,
        }}
      >
        {(preFlight?.checks ?? []).map((c) => (
          <Box
            key={c.id}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              p: 1.5,
              border: '1px solid',
              borderColor: c.passed ? 'success.main' : 'error.main',
              borderRadius: 1,
              backgroundColor: c.passed
                ? 'rgba(46, 125, 50, 0.04)'
                : 'rgba(211, 47, 47, 0.04)',
            }}
          >
            {c.passed ? (
              <CheckCircleOutlined
                sx={{ color: 'success.main', fontSize: 22, mt: 0.25 }}
              />
            ) : (
              <ErrorOutlineOutlined
                sx={{ color: 'error.main', fontSize: 22, mt: 0.25 }}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.25 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {CHECK_KEY[c.id] ? t(`system:ssoStrictMode.checkLabels.${CHECK_KEY[c.id]}`) : c.id}
                </Typography>
                {!c.passed && c.unmigrated_count != null ? (
                  <Chip
                    label={t('system:ssoStrictMode.unmigratedSuffix', { count: c.unmigrated_count })}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      borderRadius: 1,
                    }}
                    color="error"
                  />
                ) : null}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                {c.detail}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ width: '100%', my: 2 }} />

      {/* Documentation pointers */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <WarningAmberOutlined
          sx={{ color: 'warning.main', fontSize: 22, mt: 0.25 }}
        />
        <Typography variant="caption" color="text.secondary" dangerouslySetInnerHTML={{ __html: t('system:ssoStrictMode.footerNote') }} />
      </Stack>

      {/* Emergency-disable dialog */}
      <Dialog
        open={emergencyOpen}
        onClose={() => !emergencyDisabling && setEmergencyOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t('system:ssoStrictMode.emergencyDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('system:ssoStrictMode.emergencyDialog.body')}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label={t('system:ssoStrictMode.emergencyDialog.reasonLabel')}
            value={emergencyReason}
            onChange={(e) => setEmergencyReason(e.target.value)}
            placeholder={t('system:ssoStrictMode.emergencyDialog.reasonPlaceholder')}
            inputProps={{ maxLength: 500 }}
            helperText={t('system:ssoStrictMode.emergencyDialog.reasonHelper', { length: emergencyReason.length })}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEmergencyOpen(false)}
            disabled={emergencyDisabling}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('system:ssoStrictMode.emergencyDialog.cancel')}
          </Button>
          <Button
            onClick={handleEmergencyDisable}
            disabled={emergencyDisabling || !reasonValid}
            color="error"
            variant="contained"
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            {emergencyDisabling ? t('system:ssoStrictMode.emergencyDialog.disabling') : t('system:ssoStrictMode.emergencyDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default SSOStrictMode;
